import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { playlistApi } from '../../api/swagger'
import { useAppSnackbar } from '../useAppSnackbar'
import useLoadingContext from '../useLoadingContext'
import { useLoadingIds } from '../useLoadingIds'

interface UseCopyPlaylistHookResponse {
  copyPlaylist: (playlistKey: number) => void
  newPlaylistKey: number | null
}

export const useCopyPlaylist = (props?: {
  onCopyComplete?: () => void
}): UseCopyPlaylistHookResponse => {
  const { t } = useTranslation()
  const { handleError, handleMessage } = useAppSnackbar()
  const [newPlaylistKey, setNewPlaylistKey] = useState<number | null>(null)

  const {
    Playlist: { clonePlaylist: clonePlaylistLoadingId },
  } = useLoadingIds()

  const defaultErrorMessage = t(
    'ContentDetailPlaylistCard.CopyPlaylist.Error',
    'An error occurred while copying the playlist.'
  )

  const copyPlaylistAsync = async (opts?: { playlistKey?: number }) => {
    const playlistKey = opts?.playlistKey
    if (!playlistKey) return

    try {
      const response = await playlistApi.clonePlaylist({ playlistKey })
      setNewPlaylistKey(response.playlistKey)

      handleMessage(
        t(
          'ContentDetailPlaylistCard.ClonePlaylist.Success',
          'The playlist was copied successfully.'
        )
      )
    } catch (error) {
      await handleError(error, defaultErrorMessage)
    }
  }

  const { triggerFetch } = useLoadingContext({
    asyncFunction: copyPlaylistAsync,
    loadingId: clonePlaylistLoadingId,
    callback: props?.onCopyComplete,
  })

  const copyPlaylist = (playlistKey: number) => {
    triggerFetch({ playlistKey })
  }

  return { copyPlaylist, newPlaylistKey }
}
